import React, { useState, useEffect } from 'react';
import { Search, X, Users, Trophy } from 'lucide-react';
import { searchTeams } from '../data/teams';

const TeamSelectionModal = ({ isOpen, onSelectTeam, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);

  useEffect(() => {
    if (searchQuery.length > 2) {
      const results = searchTeams(searchQuery);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const handleTeamSelect = (team) => {
    setSelectedTeam(team);
  };

  const handleConfirm = () => {
    if (selectedTeam) {
      onSelectTeam(selectedTeam);
      onClose();
    }
  };

  const handleSkip = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl max-w-md w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-orange-600 text-white p-6 text-center relative">
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors"
          >
            <X size={24} />
          </button>
          <Trophy size={48} className="mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Who's Your Team?</h2>
          <p className="text-blue-100">Choose from 32 NFL teams or 60+ college programs - We'll customize everything for you!</p>
        </div>

        {/* Search Section */}
        <div className="p-6 overflow-y-auto max-h-[65vh]">
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search for your team..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 placeholder-gray-400 bg-white"
            />
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mb-6 max-h-64 overflow-y-auto">
              <h3 className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                Search Results
              </h3>
              <div className="space-y-2">
                {searchResults.map((team, index) => (
                  <button
                    key={index}
                    onClick={() => handleTeamSelect(team)}
                    className={`w-full text-left p-3 rounded-xl border-2 transition-all duration-200 ${
                      selectedTeam?.name === team.name
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-4 h-4 rounded-full flex-shrink-0"
                        style={{ backgroundColor: team.colors.primary }}
                      ></div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{team.name}</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: team.colors.primary }}
                          ></div>
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: team.colors.secondary }}
                          ></div>
                          <span className="text-sm text-gray-500">Team Colors</span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Selected Team Preview */}
          {selectedTeam && (
            <div className="mb-6 p-4 bg-gray-50 rounded-xl">
              <h3 className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                Selected Team
              </h3>
              <div 
                className="p-4 rounded-xl text-white"
                style={{ 
                  background: `linear-gradient(135deg, ${selectedTeam.colors.primary} 0%, ${selectedTeam.colors.secondary} 100%)` 
                }}
              >
                <div className="flex items-center space-x-3">
                  <Users size={24} />
                  <div>
                    <p className="font-bold text-lg">{selectedTeam.name}</p>
                    <p className="text-sm opacity-90">Site will be themed in your team colors!</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3 sticky bottom-0 bg-white pt-3">
            <button
              onClick={handleSkip}
              className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
            >
              Skip for Now
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedTeam}
              className={`flex-1 py-3 px-4 rounded-xl font-medium transition-colors ${
                selectedTeam
                  ? 'bg-gradient-to-r from-primary-600 to-orange-600 text-white hover:from-primary-700 hover:to-orange-700'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              Let's Go!
            </button>
          </div>

          {/* Help Text */}
          <p className="text-center text-sm text-gray-500 mt-4 pb-2">
            Can't find your team? Try searching by mascot, city, or nickname.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TeamSelectionModal; 